import React, { useState, useMemo } from 'react';
import Input from '../../components/Form/Input';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useBranches } from '../../hooks/useBranches';
import { PageHeader, StatsCards, FilterBar, ErrorState, type FilterConfig, type StatCard } from '../../components/Common';
import { useSalaryReportData } from './useSalaryReportData';
import { currentMonthValue } from '../Timesheet/timesheetUtils';

const fmt = (n: any) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

const SalaryReport: React.FC = () => {
  const [month, setMonth] = useState(currentMonthValue());
  const [searchTerm, setSearchTerm] = useState('');
  const [vesselFilter, setVesselFilter] = useState('all');

  const lookupQO = useMemo(() => ({ pagination: { pageSize: 200 } }), []);
  const { data: branches = [] } = useBranches(lookupQO);

  const { rows, isLoading, error, refetch } = useSalaryReportData(month);

  const filteredRows = useMemo(() => rows.filter((r) => {
    const matchesSearch = !searchTerm || r.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesVessel = vesselFilter === 'all' || String(r.branchId) === String(vesselFilter);
    return matchesSearch && matchesVessel;
  }), [rows, searchTerm, vesselFilter]);

  const totals = useMemo(() => filteredRows.reduce(
    (acc, r) => ({
      salary: acc.salary + r.salary,
      earned: acc.earned + r.earned,
      advances: acc.advances + r.cashAdvances,
      deductions: acc.deductions + r.deductions,
      net: acc.net + r.net,
    }),
    { salary: 0, earned: 0, advances: 0, deductions: 0, net: 0 }
  ), [filteredRows]);

  const stats: StatCard[] = [
    { label: 'Employees', value: filteredRows.length, color: 'blue' },
    { label: 'Total Earned', value: fmt(totals.earned), color: 'purple' },
    { label: 'Cash Advances', value: fmt(totals.advances), color: 'yellow' },
    { label: 'Deductions', value: fmt(totals.deductions), color: 'red' },
    { label: 'Net Payable', value: fmt(totals.net), color: 'green' },
  ];

  const filters: FilterConfig[] = [
    { key: 'vessel', label: 'Vessel / Headquarters', type: 'select', value: vesselFilter, onChange: setVesselFilter, options: [
      { value: 'all', label: 'All Vessels/HQ' },
      ...(branches as any[]).map((b: any) => ({ value: b.documentId || String(b.id), label: b.name })),
    ]},
  ];

  const buildExportRow = (r: any) => ({
    'Employee': r.name,
    'Vessel/HQ': r.branchName,
    'Pay Type': r.payType === 'daily' ? 'Rate per day' : 'Monthly salary',
    'Salary / Rate': r.salary,
    'Worked Days': r.hasTimesheet ? r.workedDays : '-',
    'Earned': r.earned,
    'Cash Advances': r.cashAdvances,
    'Deductions': r.deductions,
    'Net': r.net,
  });

  const handleExport = () => exportToExcel(filteredRows.map(buildExportRow), `salary_report_${month}`);
  const handlePrint = () => printAsExcel(filteredRows.map(buildExportRow), `Salary Report ${month}`);

  if (error) return <ErrorState message="Error loading salary report." onRetry={refetch} entityName="salary report" />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Salary Report"
        description="Employee salaries with timesheet days, cash advance out-transactions and deductions"
      />

      <div className="bg-white shadow rounded-lg p-4 flex flex-wrap items-end gap-4">
        <div className="w-full sm:w-48">
          <Input label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </div>
        <p className="text-sm text-gray-500 pb-2">
          Monthly employees: earned = full salary. Daily-rate employees: earned = rate per day × worked days (from the timesheet). Net = earned − advances − deductions.
        </p>
      </div>

      <StatsCards stats={stats} columns={5} />

      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search employee..."
        filters={filters} onClearFilters={() => { setSearchTerm(''); setVesselFilter('all'); }}
        totalCount={rows.length} filteredCount={filteredRows.length} entityName="employees"
        onExport={handleExport} onPrint={handlePrint}
      />

      {isLoading ? <TableSkeleton /> : (
        <div className="bg-white shadow rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Employee', 'Vessel/HQ', 'Pay Type', 'Salary / Rate', 'Worked Days', 'Earned', 'Cash Advances', 'Deductions', 'Net'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredRows.map((r) => (
                <tr key={r.key} className="hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">{r.name}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{r.branchName}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${r.payType === 'daily' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                      {r.payType === 'daily' ? 'Per day' : 'Monthly'}
                    </span>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                    {fmt(r.salary)}{r.payType === 'daily' ? <span className="text-xs text-gray-400"> /day</span> : null}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-500">{r.hasTimesheet ? r.workedDays : '-'}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-gray-900">{fmt(r.earned)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-yellow-700">{fmt(r.cashAdvances)}</td>
                  <td className="px-4 py-3 whitespace-nowrap text-red-700">{fmt(r.deductions)}</td>
                  <td className={`px-4 py-3 whitespace-nowrap font-semibold ${r.net >= 0 ? 'text-green-700' : 'text-red-700'}`}>{fmt(r.net)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredRows.length === 0 && (
            <p className="text-center text-gray-500 py-10">No employees match the selected filters</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SalaryReport;
